import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  QuickFilter,
  QuickFilterControl,
  QuickFilterTrigger,
  QuickFilterClear,
} from "@mui/x-data-grid";
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import type { ReactNode } from "react";
import type { GridCsvExportOptions } from "@mui/x-data-grid";

export interface DataGridToolbarLabels {
  columns?: string;
  filters?: string;
  download?: string;
  search?: string;
}

export interface DataGridToolbarProps {
  title?: string;
  search?: boolean;
  download?: boolean;
  filter?: boolean;
  showColumns?: boolean;
  actions?: ReactNode;
  csvOptions?: GridCsvExportOptions;
  labels?: DataGridToolbarLabels;
}

const DEFAULT_LABELS = {
  columns: "Columns",
  filters: "Filters",
  download: "Download CSV",
  search: "Search",
};

/**
 * Composable toolbar for {@link DataGridTable}, built on the MUI X v8 toolbar
 * primitives (Toolbar / ColumnsPanelTrigger / FilterPanelTrigger / ExportCsv /
 * QuickFilter). Each piece is toggled by a flag so a consumer renders only the
 * actions it needs — replacing the per-table `options` flags of mui-datatables.
 *
 * The table title is rendered on the left (matching the mui-datatables layout
 * where title and controls share one row); extra buttons (reload, create, ...)
 * are injected through `actions`.
 */
export const DataGridToolbar = ({
  title,
  search = true,
  download = true,
  filter = true,
  showColumns = true,
  actions = null,
  csvOptions,
  labels,
}: DataGridToolbarProps) => {
  const text = { ...DEFAULT_LABELS, ...labels };

  return (
    <Toolbar>
      {title && (
        <Typography
          component="div"
          sx={{ fontSize: 20, fontWeight: 500, color: "text.primary", mr: 2 }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {search && (
        <QuickFilter>
          <QuickFilterTrigger
            render={(props, state) =>
              state.expanded ? <></> : (
                <Tooltip title={text.search}>
                  <ToolbarButton {...props} aria-label={text.search}>
                    <SearchIcon fontSize="small" />
                  </ToolbarButton>
                </Tooltip>
              )
            }
          />
          <QuickFilterControl
            render={({ ref, ...controlProps }, state) => (
              <TextField
                {...controlProps}
                inputRef={ref}
                name="datagrid-quick-filter"
                size="small"
                placeholder={text.search}
                aria-label={text.search}
                sx={{
                  width: state.expanded ? 200 : 0,
                  opacity: state.expanded ? 1 : 0,
                  transition: (theme) =>
                    theme.transitions.create(["width", "opacity"]),
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: state.expanded ? (
                      <InputAdornment position="end">
                        <QuickFilterClear
                          render={(clearProps) => (
                            <IconButton
                              {...clearProps}
                              size="small"
                              edge="end"
                              aria-label={text.search}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        />
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            )}
          />
        </QuickFilter>
      )}

      {download && (
        <Tooltip title={text.download}>
          <ExportCsv
            options={csvOptions}
            render={(props) => <ToolbarButton {...props} aria-label={text.download} />}
          >
            <FileDownloadIcon fontSize="small" />
          </ExportCsv>
        </Tooltip>
      )}

      {showColumns && (
        <Tooltip title={text.columns}>
          <ColumnsPanelTrigger
            render={(props) => <ToolbarButton {...props} aria-label={text.columns} />}
          >
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
      )}

      {filter && (
        <Tooltip title={text.filters}>
          <FilterPanelTrigger
            render={(props) => <ToolbarButton {...props} aria-label={text.filters} />}
          >
            <FilterListIcon fontSize="small" />
          </FilterPanelTrigger>
        </Tooltip>
      )}

      {actions}
    </Toolbar>
  );
};
